/** Shared package rules; keep web/mobile copies in sync. Money is in major units. */
export function money(amount: number, currency: string = "ngn") {
  return new Intl.NumberFormat("en-NG", {
    style: "currency",
    currency: currency.toUpperCase(),
    maximumFractionDigits: 2,
  }).format(amount);
}
export function unitLabel(unit: string, quantity: number) {
  const label =
    (
      {
        YARD: "yard",
        METRE: "metre",
        PIECE: "piece",
        PAIR: "pair",
        SET: "set",
      } as Record<string, string>
    )[unit] ?? unit.toLowerCase();
  return `${label}${quantity === 1 ? "" : "s"}`;
}
export function packageError(error: unknown): string {
  const err = error as {
    message?: string;
    graphQLErrors?: {
      extensions?: {
        code?: string;
        minimum_stock?: number;
        available_quantity?: number;
      };
    }[];
    errors?: { extensions?: { code?: string } }[];
  };
  const ext =
    err?.graphQLErrors?.[0]?.extensions ?? err?.errors?.[0]?.extensions;
  const messages: Record<string, string> = {
    PACKAGE_VERSION_CONFLICT:
      "This package has changed. Reload the latest details before continuing.",
    INSUFFICIENT_STOCK:
      "There aren't enough packages left. Refresh availability and adjust your selection.",
    CHECKOUT_EXPIRED:
      "Your reservation has expired. Check the payment status before starting a new checkout.",
    IDEMPOTENCY_CONFLICT:
      "This checkout belongs to a different selection. Cancel it before starting again.",
    ASOEBI_DEADLINE_PASSED:
      "The purchase deadline for this asoebi group has passed.",
    PACKAGE_NOT_AVAILABLE: "This package is no longer available for purchase.",
    CURRENCY_MISMATCH: "These items cannot be purchased in the same currency.",
    FORBIDDEN: "Please sign in with an account that can access this order.",
  };
  if (ext?.code && messages[ext.code]) return messages[ext.code];
  return err?.message || "Something went wrong. Please try again.";
}
export function packageCount(value: number, available: number) {
  return Math.max(
    0,
    Math.min(1000, available, Math.floor(Number.isFinite(value) ? value : 0))
  );
}
export function isExpired(expiresAt?: string | null, now = Date.now()) {
  return (
    !!expiresAt &&
    (!Number.isFinite(Date.parse(expiresAt)) || Date.parse(expiresAt) <= now)
  );
}
export function checkoutMessage(status: string) {
  switch (status) {
    case "PAID":
      return "Your order is confirmed. Your complete packages will be prepared for you.";
    case "REQUIRES_RESOLUTION":
      return "Your payment was received. Support will contact you to resolve fulfilment or a refund. Please do not pay again.";
    case "EXPIRED":
      return "Your reservation has expired. If you have paid, keep checking your payment status before starting again.";
    case "CANCELLED":
      return "This checkout was cancelled. If you have paid, check the payment status before starting again.";
    default:
      return "Payment is pending. Complete payment before your reservation expires. If you have already paid, check its status before trying again.";
  }
}
export function validatePackage(
  draft: {
    name: string;
    price: string;
    stock: string;
    cover_image: string;
    items: { name: string; quantity: string; unit: string }[];
  },
  publishing: boolean
) {
  if (!draft.name.trim()) return "Enter a package name.";
  if (
    draft.price !== "" &&
    (!Number.isFinite(Number(draft.price)) ||
      Number(draft.price) < 0 ||
      !/^\d+(\.\d{1,2})?$/.test(draft.price))
  )
    return "Enter a valid price with up to two decimal places.";
  if (
    draft.stock !== "" &&
    (!Number.isInteger(Number(draft.stock)) || Number(draft.stock) < 0)
  )
    return "Package capacity must be a whole number of zero or more.";
  for (const item of draft.items) {
    if (!item.name.trim()) return "Give every included item a name.";
    const quantity = Number(item.quantity);
    if (!Number.isFinite(quantity) || quantity <= 0)
      return `Enter a positive quantity for ${item.name}.`;
    if (
      ["YARD", "METRE"].includes(item.unit)
        ? !/^\d+(\.\d{1,2})?$/.test(item.quantity)
        : !Number.isInteger(quantity)
    )
      return `Check the quantity for ${item.name}. Yards/metres allow two decimal places; other units need whole numbers.`;
  }
  if (
    publishing &&
    (!(Number(draft.price) > 0) ||
      !(Number(draft.stock) > 0) ||
      !draft.cover_image ||
      !draft.items.length)
  )
    return "Add a cover image, at least one item, a price above zero and package capacity before publishing.";
  return null;
}
